import { createTheme, responsiveFontSizes } from '@mui/material/styles';

// FPI brand colors (ported from the original styles.css :root tokens).
export const BRAND = {
	navy: '#1F2B3E',
	navyLight: '#2a3a52',
	orange: '#E8531E',
	orangeLight: '#f06535',
	bg: '#f4f5f7',
	surface: '#ffffff',
	border: '#dde1e7',
	text: '#1a1a2e',
	muted: '#6b7280',
};

// Allow `color="navy"` on Button / IconButton etc.
declare module '@mui/material/styles' {
	interface Palette {
		navy: Palette['primary'];
	}
	interface PaletteOptions {
		navy?: PaletteOptions['primary'];
	}
}
declare module '@mui/material/Button' {
	interface ButtonPropsColorOverrides {
		navy: true;
	}
}
declare module '@mui/material/IconButton' {
	interface IconButtonPropsColorOverrides {
		navy: true;
	}
}

let baseTheme = createTheme({
	palette: {
		mode: 'light',
		primary: { main: BRAND.orange, light: BRAND.orangeLight, contrastText: '#ffffff' },
		secondary: { main: BRAND.navy, light: BRAND.navyLight, contrastText: '#ffffff' },
		navy: { main: BRAND.navy, light: BRAND.navyLight, contrastText: '#ffffff' },
		success: { main: '#16a34a' },
		warning: { main: '#b45309' },
		error: { main: '#dc2626' },
		info: { main: '#1d4ed8' },
		background: { default: BRAND.bg, paper: BRAND.surface },
		text: { primary: BRAND.text, secondary: BRAND.muted },
		divider: BRAND.border,
	},
	shape: { borderRadius: 10 },
	typography: {
		fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
		fontSize: 14,
		button: { textTransform: 'none', fontWeight: 600 },
		h6: { fontWeight: 700 },
		overline: { fontWeight: 700, letterSpacing: 0.6 },
	},
	components: {
		MuiCard: {
			defaultProps: { variant: 'outlined' },
			styleOverrides: {
				root: { borderColor: BRAND.border, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
			},
		},
		MuiButton: {
			defaultProps: { disableElevation: true },
			styleOverrides: {
				// Comfortable touch targets without bloating dense desktop toolbars.
				root: { minHeight: 40 },
				sizeSmall: { minHeight: 34 },
			},
		},
		MuiIconButton: {
			styleOverrides: {
				root: { padding: 9 },
			},
		},
		MuiChip: {
			styleOverrides: {
				root: { fontWeight: 600 },
			},
		},
		MuiTextField: {
			defaultProps: { size: 'small', fullWidth: true },
		},
		MuiInputBase: {
			styleOverrides: {
				// 16px input font on phones prevents iOS Safari zoom-on-focus.
				input: {
					'@media (max-width:600px)': { fontSize: 16 },
				},
			},
		},
		MuiListItemButton: {
			styleOverrides: {
				root: { minHeight: 48, borderRadius: 8 },
			},
		},
		MuiBottomNavigationAction: {
			styleOverrides: {
				root: { minWidth: 0, paddingTop: 8, paddingBottom: 8 },
				label: { fontSize: 11, '&.Mui-selected': { fontSize: 11 } },
			},
		},
		MuiTableCell: {
			styleOverrides: {
				root: { paddingTop: 10, paddingBottom: 10 },
				head: {
					backgroundColor: BRAND.navy,
					color: '#fff',
					fontSize: 11,
					fontWeight: 600,
					letterSpacing: 0.5,
					textTransform: 'uppercase',
					whiteSpace: 'nowrap',
				},
			},
		},
	},
});

baseTheme = responsiveFontSizes(baseTheme);

export const theme = baseTheme;
